import React, { useState } from 'react';
import { PhoneIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_TEL, CTAButton } from './shared';
// 03 — HERO · [Branding] [Conversion] [Lead Gen] [SEO/GEO]
// Form-forward layout: copy + proof left, an elevated "What happened?" claim
// starter card right so the lead action begins in the hero. Exactly one H1.
// Choices map to the firm's real claim taxonomy and route into the full
// Quick Claim form at #quick-claim-form.
const choices = [{
  label: 'Motor vehicle accident',
  category: 'road'
}, {
  label: 'Workplace injury',
  category: 'work'
}, {
  label: 'Slip, fall or public place',
  category: 'public'
}, {
  label: 'Serious or catastrophic injury',
  category: 'serious'
}, {
  label: "I'm not sure",
  category: 'unsure'
}] as const;
export function Hero() {
  const [active, setActive] = useState<string | null>(null);
  return <section className="w-full bg-canvas" data-id="element-204">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-10 px-5 py-16 sm:px-6 md:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:px-8 lg:py-24" data-id="element-205">
        {/* Copy column */}
        <div className="flex flex-col justify-center" data-id="element-206">
          <span className="text-small font-semibold uppercase tracking-[0.16em] text-gold" data-id="element-207">
            Perth personal injury lawyers since 1990
          </span>

          <h1 className="mt-5 font-serif text-[44px] font-medium leading-[1.03] tracking-[-0.02em] text-forest md:text-display" data-id="element-208">
            Injured in Perth? Get clear advice and strong representation.
          </h1>

          <p className="mt-6 max-w-xl text-body-lg text-ink" data-id="element-209">
            If an injury has affected your health, work or future, get a clear
            explanation of your rights and the next step from an established
            Perth injury law firm.
          </p>

          <div className="mt-7 flex flex-col gap-2.5" data-id="element-210">
            <span className="inline-flex items-center gap-2 text-small text-slate" data-id="element-211">
              <span className="flex" aria-hidden="true" data-id="element-212">
                {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="h-4 w-4 fill-gold text-gold" strokeWidth={0} data-id="element-213" />)}
              </span>
              <span className="font-semibold text-ink" data-id="element-214">4.6 stars</span>
              from 231 Google reviews
            </span>
            <span className="text-small text-slate" data-id="element-215">
              Free, confidential initial discussion · No obligation
            </span>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-216">
            <CTAButton href="#quick-claim-form" className="px-7" data-id="element-217">
              Check My Claim
            </CTAButton>
            <CTAButton href={PHONE_TEL} variant="secondary" className="px-6" data-id="element-218">
              <PhoneIcon aria-hidden="true" className="h-4 w-4" strokeWidth={1.75} data-id="element-219" />
              Call {PHONE_DISPLAY}
            </CTAButton>
          </div>

          <p className="mt-6 text-small text-slate" data-id="element-220">
            453 Beaufort Street, Highgate · Heritage Perth office with free
            street parking
          </p>
        </div>

        {/* Claim-starter card — the lead action begins here */}
        <div className="lg:justify-self-end lg:max-w-md w-full" data-id="element-221">
          <div className="rounded-frame border border-ink/5 bg-paper p-7 shadow-card-lg md:p-9" data-id="element-222">
            <h2 className="font-serif text-h3 text-forest" data-id="element-223">What happened?</h2>
            <p className="mt-2 text-small text-slate" data-id="element-224">
              Choose what best matches your situation — every path reaches a
              lawyer who can help.
            </p>

            <div className="mt-6 flex flex-col gap-3" data-id="element-225">
              {choices.map(c => {
              const isActive = active === c.category;
              return <a key={c.category} href="#quick-claim-form" onClick={() => setActive(c.category)} aria-pressed={isActive} className={`group flex items-center justify-between gap-3 rounded-btn border px-5 py-4 text-left text-body font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal ${isActive ? 'border-forest bg-forest text-paper' : 'border-mist bg-canvas text-ink hover:border-forest hover:bg-mist'}`} data-id="element-226">
                    {c.label}
                    <ArrowRightIcon aria-hidden="true" className={`h-4 w-4 shrink-0 transition-transform group-hover:translate-x-1 ${isActive ? 'text-paper' : 'text-signal'}`} strokeWidth={1.75} data-id="element-227" />
                  </a>;
            })}
            </div>

            <p className="mt-6 text-center text-small text-slate" data-id="element-228">
              Free &amp; confidential · Start your claim in under a minute
            </p>
          </div>
        </div>
      </div>
    </section>;
}